import { Box, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { GuestJourneyDetailsRow } from 'api/reports-service/guest-journey-reports.types';
import IncDataGrid from 'components/inc-data-grid/inc-data-grid.component';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GUEST_JOURNEY_DEFINITIONS } from '../guest-journey-definitions';
import DetailsSegmentExportButton from './details-segment-export-button/details-segment-export-button.component';

export type DetailsSegmentTableProps = {
  /** Visit-tier name shown as the section header (e.g. Superfan / Regular). */
  visitTierName: string;
  rows: GuestJourneyDetailsRow[];
  loading?: boolean;
  /** Active Details filters, forwarded to each row's customer-list export. */
  exportParams?: Record<string, unknown>;
  'data-testid'?: string;
};

const DetailsSegmentTable = ({
  visitTierName,
  rows,
  loading = false,
  exportParams,
  'data-testid': dataTestId = 'Details.SegmentTable',
}: DetailsSegmentTableProps) => {
  const { t } = useTranslation();

  const columns: GridColDef[] = useMemo(
    () => [
      {
        // BE-resolved spend-tier name, rendered verbatim.
        field: 'stage',
        headerName: t('page.guestJourney.details.table.columns.stage'),
        flex: 1,
        minWidth: 160,
        sortable: false,
      },
      {
        field: 'currentTotalMembers',
        headerName:
          GUEST_JOURNEY_DEFINITIONS.detailsCurrentTotalMembers.getLabel(t),
        flex: 1,
        minWidth: 160,
        sortable: false,
      },
      {
        field: 'totalVisits',
        headerName: GUEST_JOURNEY_DEFINITIONS.detailsTotalVisits.getLabel(t),
        flex: 1,
        minWidth: 140,
        sortable: false,
      },
      {
        field: 'totalSpendNetSales',
        headerName: GUEST_JOURNEY_DEFINITIONS.detailsTotalSpend.getLabel(t),
        flex: 1,
        minWidth: 180,
        sortable: false,
      },
      {
        field: 'averageOrderSize',
        headerName:
          GUEST_JOURNEY_DEFINITIONS.detailsAverageOrderSize.getLabel(t),
        flex: 1,
        minWidth: 160,
        sortable: false,
      },
      {
        field: 'medianDaysBetweenVisits',
        headerName:
          GUEST_JOURNEY_DEFINITIONS.detailsMedianDaysBetweenVisits.getLabel(t),
        flex: 1,
        minWidth: 200,
        sortable: false,
      },
      {
        // Per-segment customer-list export (sc-357751 QA · activity 368127).
        field: 'export',
        headerName: '',
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        renderCell: params => (
          <DetailsSegmentExportButton
            segment={params.row.segmentKey}
            params={exportParams}
            data-testid={`${dataTestId}.Export.${params.row.stage}`}
          />
        ),
      },
    ],
    [t, dataTestId, exportParams],
  );

  return (
    <Stack spacing={1} data-testid={dataTestId}>
      <Typography variant="h2" data-testid={`${dataTestId}.Header`}>
        {visitTierName}
      </Typography>
      <Box>
        <IncDataGrid
          data-testid={`${dataTestId}.Grid`}
          columns={columns}
          rows={rows}
          loading={loading}
          getRowId={row => row.stage}
          hideFooter
          disableColumnReorder
          disableColumnMenu
          disableRowSelectionOnClick
          autoHeight
        />
      </Box>
    </Stack>
  );
};

export default DetailsSegmentTable;
